import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ChefHat, Users, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [household, setHousehold] = useState(null);
  const [members, setMembers] = useState([{
    name: '',
    age: null,
    gender: 'other',
    religious_diet: 'none',
    diet_tags: [],
    allergies: [],
    is_baby: false,
    goal: 'maintain'
  }]);
  const [loading, setLoading] = useState(false);
  const user = location.state?.user;
  const householdId = location.state?.household_id;
  const addMember = () => {
    setMembers([...members, {
      name: '',
      age: null,
      gender: 'other',
      religious_diet: 'none',
      diet_tags: [],
      allergies: [],
      is_baby: false,
      goal: 'maintain'
    }]);
  };
  const removeMember = (index) => {
    if (members.length > 1) {
      setMembers(members.filter((_, i) => i !== index));
    }
  };
  const updateMember = (index, field, value) => {
    const updated = [...members];
    updated[index][field] = value;
    setMembers(updated);
  };
  const toggleDietTag = (index, tag) => {
    const updated = [...members];
    const tags = updated[index].diet_tags;
    if (tags.includes(tag)) {
      updated[index].diet_tags = tags.filter(t => t !== tag);
    } else {
      updated[index].diet_tags = [...tags, tag];
    }
    setMembers(updated);
  };
  const toggleAllergy = (index, allergy) => {
    const updated = [...members];
    const allergies = updated[index].allergies || [];
    if (allergies.includes(allergy)) {
      updated[index].allergies = allergies.filter(a => a !== allergy);
    } else {
      updated[index].allergies = [...allergies, allergy];
    }
    setMembers(updated);
  };
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const householdResponse = await axios.get(`${API}/households`, { withCredentials: true });
      const currentHousehold = householdResponse.data.household;
      if (!currentHousehold) {
        toast.error('No household found. Please log in again.');
        setLoading(false);
        return;
      }
      for (const member of members) {
        if (!member.name) {
          toast.error('Please fill in all member names');
          setLoading(false);
          return;
        }
        const memberData = {
          household_id: currentHousehold.household_id,
          name: member.name,
          age: member.age && !isNaN(member.age) ? parseInt(member.age) : null,
          gender: member.gender || 'other',
          religious_diet: member.religious_diet || 'none',
          diet_tags: member.diet_tags || [],
          allergies: member.allergies || [],
          is_baby: member.is_baby || false,
          goal: member.goal || 'maintain'
        };
        await axios.post(`${API}/family-members`, memberData, { withCredentials: true });
      }
      toast.success('Family setup complete!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding error:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        toast.error(`Error: ${error.response.data?.detail || 'Failed to save family members'}`);
      } else {
        toast.error('Failed to save family members. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="flex justify-center mb-8">
          <div className="bg-primary rounded-full p-4">
            <Users className="w-10 h-10 text-white" />
          </div>
        </div>
        <h1 className="text-4xl font-heading font-bold text-center mb-3 text-[#2C3E2D]" data-testid="onboarding-title">
          Set Up Your Family
        </h1>
        <p className="text-center text-gray-600 mb-10">Add family members to get personalized meal plans</p>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Family Members</span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={addMember}
                data-testid="add-member-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {members.map((member, index) => (
              <div key={index} className="p-6 bg-gray-50 rounded-lg space-y-4" data-testid={`member-card-${index}`}>
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-lg">Member {index + 1}</h3>
                  {members.length > 1 && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => removeMember(index)}
                      data-testid={`remove-member-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`name-${index}`}>Name</Label>
                    <Input
                      id={`name-${index}`}
                      value={member.name}
                      onChange={(e) => updateMember(index, 'name', e.target.value)}
                      placeholder="Full name"
                      data-testid={`member-name-${index}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`age-${index}`}>Age</Label>
                    <Input
                      id={`age-${index}`}
                      type="number"
                      value={member.age === null ? '' : member.age}
                      onChange={(e) => updateMember(index, 'age', e.target.value === '' ? null : parseInt(e.target.value))}
                      placeholder="Age (optional)"
                      data-testid={`member-age-${index}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`gender-${index}`}>Gender</Label>
                    <Select value={member.gender} onValueChange={(v) => updateMember(index, 'gender', v)}>
                      <SelectTrigger data-testid={`member-gender-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor={`religious-${index}`}>Religious Diet</Label>
                    <Select value={member.religious_diet} onValueChange={(v) => updateMember(index, 'religious_diet', v)}>
                      <SelectTrigger data-testid={`member-religious-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="islam">Halal (Islam)</SelectItem>
                        <SelectItem value="jewish">Kosher (Jewish)</SelectItem>
                        <SelectItem value="christian">Christian</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Dietary Preferences</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['vegetarian', 'vegan', 'pescatarian', 'gluten-free', 'lactose-free', 'dairy-free', 'low-carb', 'keto', 'paleo', 'mediterranean', 'low-sodium', 'sugar-free', 'organic'].map(tag => (
                      <label key={tag} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={member.diet_tags.includes(tag)}
                          onCheckedChange={() => toggleDietTag(index, tag)}
                          data-testid={`member-${index}-diet-${tag}`}
                        />
                        <span className="text-sm capitalize">{tag}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Allergies &amp; Intolerances</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['peanuts', 'tree-nuts', 'shellfish', 'fish', 'eggs', 'milk', 'soy', 'wheat', 'gluten', 'sesame', 'mustard', 'celery', 'lupin', 'molluscs', 'sulfites'].map(allergy => (
                      <label key={allergy} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={member.allergies?.includes(allergy) || false}
                          onCheckedChange={() => toggleAllergy(index, allergy)}
                          data-testid={`member-${index}-allergy-${allergy}`}
                        />
                        <span className="text-sm capitalize">{allergy}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={member.is_baby}
                    onCheckedChange={(checked) => updateMember(index, 'is_baby', checked)}
                    data-testid={`member-${index}-is-baby`}
                  />
                  <Label>Baby/Toddler (needs soft food)</Label>
                </div>
              </div>
            ))}
            <Button
              className="w-full bg-primary hover:bg-primary-hover text-white py-6 text-lg rounded-full"
              onClick={handleSubmit}
              disabled={loading}
              data-testid="submit-onboarding-btn"
            >
              {loading ? 'Saving...' : 'Complete Setup'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
export default OnboardingPage;
